import React, { useEffect, useState } from "react";

import { InvestorAutocomplete } from "components/Autocompletes/InvestorAutocomplete";
import { useActionHook } from "hooks/shared-hooks/use-action-hook";
import { useDealActions } from "store";
import { IFileRepo } from "store/types";

interface Props {
  fileData: IFileRepo;
  dealInvestors: { id: number; name: string; is_splittable?: boolean }[];
  investorsLoading?: boolean;
  readOnly?: boolean;
  onFileUpdated?: (file: IFileRepo) => void;
}

/**
 * "Investment Entity" attribution for a K-1 file — the same investor autocomplete the regular
 * extractor uses ("Attributed to"), just relabelled. Value is the file's investor; changing it
 * PATCHes the file (`updateFileRepoAction`) and bubbles the updated file up via `onFileUpdated`.
 */
export const K1InvestmentEntity: React.FC<Props> = ({
  fileData,
  dealInvestors,
  investorsLoading,
  readOnly,
  onFileUpdated
}) => {
  const { updateFileRepoAction } = useDealActions();
  const { action: patchFile } = useActionHook(updateFileRepoAction, {
    onSuccess: (data) => onFileUpdated?.(data)
  });

  const [investorId, setInvestorId] = useState<number | null>(fileData.investor?.id ?? null);

  useEffect(() => {
    setInvestorId(fileData.investor?.id ?? null);
    // Re-sync only when switching files (see the regular extractor's parameters table).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileData.id]);

  const handleChange = (inv: { id: number; name: string } | null) => {
    setInvestorId(inv?.id ?? null);
    patchFile({ fileId: fileData.id, investor_id: inv?.id ?? null, all_investors: false, investor_ids: [] });
  };

  return (
    <InvestorAutocomplete
      label="Investment Entity"
      loading={investorsLoading}
      disabled={readOnly}
      dealInvestors={dealInvestors}
      preselectedInvestorId={investorId}
      onChange={handleChange}
    />
  );
};
